import type {FC} from 'react';
import {useRoutes} from 'react-router-dom';
import {Helmet} from 'react-helmet-async';
import {Provider as ReduxProvider} from 'react-redux';
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import 'react-quill/dist/quill.snow.css'; // Remove if react-quill is not used
import 'simplebar-react/dist/simplebar.min.css'; // Remove if simplebar is not used
import './locales/i18n';  // Remove if locales are not used
import {RTL} from 'src/components/rtl';
import {PWMSplashScreen} from "src/PWM/components/PWMSplashScreen";
import {Toaster} from 'src/components/toaster';
import {SettingsConsumer, SettingsProvider} from 'src/contexts/settings-context';
import {useNprogress} from 'src/hooks/use-nprogress';
import {PWMRoutes as routes} from 'src/PWM/PWMRoutes';
import {store} from 'src/store';
import {createTheme} from 'src/theme';
import {AuthContext, AuthProvider} from "src/PWM/context/auth-context";
import PWMErrorBoundary from './components/PWMErrorBoundary';
import AuthGuard from "./guards/auth-guard";
import {ApiProvider} from "src/PWM/context/api-context";
import {GuestGuard} from "src/PWM/guards/guest-guard";
import {NotificationProvider} from './context/notification-context';
import {AppDataProvider} from './context/appdata-context';

export const PWMApp: FC = () => {
  useNprogress();

  const element = useRoutes(routes);
  return (
    <ReduxProvider store={store}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthProvider>
          <AuthContext.Consumer>
            {(auth) => {
              return (
                <SettingsProvider>
                  <SettingsConsumer>
                    {(settings) => {
                      // Prevent theme flicker when restoring custom settings from browser storage
                      if (!settings.isInitialized) {
                        // return null;
                      }

                      const theme = createTheme({
                        colorPreset: settings.colorPreset,
                        contrast: settings.contrast,
                        direction: settings.direction,
                        paletteMode: settings.paletteMode,
                        responsiveFontSizes: settings.responsiveFontSizes
                      });

                      // Prevent guards from redirecting
                      const showSlashScreen = !auth.isInitialized;
                      const showLogin = !auth.isSignedIn;
                      return (
                        <ThemeProvider theme={theme}>
                          <Helmet>
                            <meta
                              name="color-scheme"
                              content={settings.paletteMode}
                            />
                            <meta
                              name="theme-color"
                              content={theme.palette.neutral[900]}
                            />
                          </Helmet>
                          <PWMErrorBoundary>
                            <RTL direction={settings.direction}>
                            <CssBaseline/>
                            {
                              showSlashScreen
                                ? <PWMSplashScreen/>
                                : showLogin
                                  ? <GuestGuard>{element}</GuestGuard>
                                  : (
                                    <AuthGuard>
                                      <ApiProvider>
                                        <AppDataProvider>
                                          <NotificationProvider>
                                            {element}
                                          </NotificationProvider>
                                        </AppDataProvider>
                                      </ApiProvider>
                                    </AuthGuard>
                                  )
                            }
                            <Toaster/>
                          </RTL>
                          </PWMErrorBoundary>
                        </ThemeProvider>
                      );
                    }}
                  </SettingsConsumer>
                </SettingsProvider>
              )
            }}
          </AuthContext.Consumer>
        </AuthProvider>
      </LocalizationProvider>
    </ReduxProvider>
  );
};
